/**
 * Agent Marketplace Page
 * UF-008: Agent Marketplace UI
 *
 * Main marketplace interface with hero, filters, and agent grid
 */

import { useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { MarketplaceAgent, MarketplaceFilters as FiltersType, SortOption } from "./types";
import { MarketplaceFilters } from "./MarketplaceFilters";
import { AgentCard } from "./AgentCard";
import { AgentDetailModal } from "./AgentDetailModal";
import { MOCK_AGENTS } from "./mockData";

// =============================================================================
// Types
// =============================================================================

interface MarketplacePageProps {
  onNavigateToConsole?: () => void;
}

// =============================================================================
// Animation Variants
// =============================================================================

const heroVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const heroItemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

const gridContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.2,
    },
  },
};

// =============================================================================
// Search Bar Component
// =============================================================================

function SearchBar({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <motion.div
      variants={heroItemVariants}
      className="relative w-full max-w-2xl mx-auto"
    >
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search agents by name, capability, or keyword..."
          className="
            w-full bg-hvme-bg-surface/80 backdrop-blur-sm
            border border-hvme-border rounded-2xl
            pl-12 pr-4 py-4 font-body text-base text-hvme-text-primary
            placeholder-hvme-text-muted
            focus:outline-none focus:border-hvme-primary/40 focus:ring-1 focus:ring-hvme-primary/20
            hover:border-hvme-border-hover
            transition-all duration-200
          "
        />
        <svg
          className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-hvme-text-muted"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        {value && (
          <button
            onClick={() => onChange("")}
            className="
              absolute right-4 top-1/2 -translate-y-1/2
              w-6 h-6 rounded-full bg-hvme-bg-mid
              flex items-center justify-center
              text-hvme-text-muted hover:text-hvme-text-primary
              transition-colors duration-200
            "
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </motion.div>
  );
}

// =============================================================================
// Filter Toggle Button (Mobile)
// =============================================================================

function FilterToggleButton({
  isOpen,
  onClick,
  activeCount,
}: {
  isOpen: boolean;
  onClick: () => void;
  activeCount: number;
}) {
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="
        flex items-center gap-2 px-4 py-2.5 rounded-xl
        bg-hvme-bg-surface border border-hvme-border
        text-hvme-text-secondary hover:text-hvme-text-primary
        hover:border-hvme-border-hover
        transition-all duration-200
      "
    >
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
      </svg>
      <span className="font-medium text-sm">Filters</span>
      {activeCount > 0 && (
        <span className="w-5 h-5 rounded-full bg-hvme-primary text-white text-xs font-medium flex items-center justify-center">
          {activeCount}
        </span>
      )}
    </motion.button>
  );
}

// =============================================================================
// Empty State Component
// =============================================================================

function EmptyState({ onClearFilters }: { onClearFilters: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center py-20 text-center"
    >
      <div className="w-20 h-20 rounded-full bg-hvme-bg-mid flex items-center justify-center mb-6">
        <svg className="w-10 h-10 text-hvme-text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
      </div>
      <h3 className="font-heading font-bold text-xl text-hvme-text-primary mb-2">
        No agents found
      </h3>
      <p className="text-hvme-text-secondary mb-6 max-w-sm">
        Try adjusting your search or filters to find what you&apos;re looking for.
      </p>
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onClearFilters}
        className="
          px-6 py-2.5 rounded-xl font-body text-sm font-medium
          bg-hvme-primary/10 text-hvme-primary border border-hvme-primary/20
          hover:bg-hvme-primary/20 hover:border-hvme-primary/40
          transition-all duration-200
        "
      >
        Clear Filters
      </motion.button>
    </motion.div>
  );
}

// =============================================================================
// Results Header
// =============================================================================

function ResultsHeader({
  count,
  sortBy,
  onSortChange,
}: {
  count: number;
  sortBy: SortOption;
  onSortChange: (sort: SortOption) => void;
}) {
  const sortOptions = [
    { value: "popular", label: "Most Popular" },
    { value: "newest", label: "Newest" },
    { value: "rating", label: "Highest Rated" },
    { value: "price-low", label: "Price: Low to High" },
    { value: "price-high", label: "Price: High to Low" },
  ];

  return (
    <div className="flex items-center justify-between mb-6">
      <p className="text-sm text-hvme-text-secondary">
        Showing <span className="font-medium text-hvme-text-primary">{count}</span> agents
      </p>
      <div className="hidden md:flex items-center gap-2">
        <span className="text-sm text-hvme-text-muted">Sort by:</span>
        <select
          value={sortBy}
          onChange={(e) => onSortChange(e.target.value as SortOption)}
          className="
            appearance-none bg-transparent
            text-sm font-medium text-hvme-text-primary
            cursor-pointer focus:outline-none
          "
        >
          {sortOptions.map((option) => (
            <option key={option.value} value={option.value} className="bg-hvme-bg-surface">
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

// =============================================================================
// Agent Grid
// =============================================================================

function AgentGrid({
  agents,
  onSelectAgent,
}: {
  agents: MarketplaceAgent[];
  onSelectAgent: (agent: MarketplaceAgent) => void;
}) {
  return (
    <motion.div
      variants={gridContainerVariants}
      initial="hidden"
      animate="visible"
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
    >
      {agents.map((agent, index) => (
        <AgentCard
          key={agent.id}
          agent={agent}
          onClick={() => onSelectAgent(agent)}
          index={index}
        />
      ))}
    </motion.div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function MarketplacePage({ onNavigateToConsole }: MarketplacePageProps) {
  // State
  const [filters, setFilters] = useState<FiltersType>({
    search: "",
    categories: [],
    pricingModels: [],
    minRating: null,
    sortBy: "popular",
  });
  const [selectedAgent, setSelectedAgent] = useState<MarketplaceAgent | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(true);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  // Filter and sort agents
  const filteredAgents = useMemo(() => {
    let result = [...MOCK_AGENTS];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(
        (agent) =>
          agent.name.toLowerCase().includes(searchLower) ||
          agent.shortDescription.toLowerCase().includes(searchLower) ||
          agent.tags.some((tag) => tag.toLowerCase().includes(searchLower))
      );
    }

    // Category filter
    if (filters.categories.length > 0) {
      result = result.filter((agent) => filters.categories.includes(agent.category));
    }

    // Pricing filter
    if (filters.pricingModels.length > 0) {
      result = result.filter((agent) => filters.pricingModels.includes(agent.pricing.model));
    }

    // Rating filter
    if (filters.minRating !== null) {
      result = result.filter((agent) => agent.rating >= filters.minRating!);
    }

    // Sort
    switch (filters.sortBy) {
      case "popular":
        result.sort((a, b) => b.downloads - a.downloads);
        break;
      case "newest":
        result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case "rating":
        result.sort((a, b) => b.rating - a.rating);
        break;
      case "price-low":
        result.sort((a, b) => {
          const priceA = a.pricing.price ?? 0;
          const priceB = b.pricing.price ?? 0;
          return priceA - priceB;
        });
        break;
      case "price-high":
        result.sort((a, b) => {
          const priceA = a.pricing.price ?? 0;
          const priceB = b.pricing.price ?? 0;
          return priceB - priceA;
        });
        break;
    }

    return result;
  }, [filters]);

  // Active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.categories.length > 0) count++;
    if (filters.pricingModels.length > 0) count++;
    if (filters.minRating !== null) count++;
    return count;
  }, [filters]);

  // Handlers
  const handleSelectAgent = useCallback((agent: MarketplaceAgent) => {
    setSelectedAgent(agent);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedAgent(null), 300);
  }, []);

  const handlePurchase = useCallback((agent: MarketplaceAgent) => {
    console.log("Purchase agent:", agent.id);
    // In production, this would trigger a payment flow
    alert(`Purchasing ${agent.name}...`);
  }, []);

  const handleDeploy = useCallback((agent: MarketplaceAgent) => {
    console.log("Deploy agent:", agent.id);
    // In production, this would navigate to deployment flow
    onNavigateToConsole?.();
  }, [onNavigateToConsole]);

  const clearFilters = useCallback(() => {
    setFilters({
      search: "",
      categories: [],
      pricingModels: [],
      minRating: null,
      sortBy: "popular",
    });
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        {/* Background Decoration */}
        <div
          className="absolute inset-0 z-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 60% 40% at 50% 30%, rgba(117, 142, 255, 0.1) 0%, transparent 60%), radial-gradient(ellipse 50% 30% at 70% 60%, rgba(214, 30, 244, 0.08) 0%, transparent 50%)",
          }}
        />

        {/* Hero Content */}
        <motion.div
          variants={heroVariants}
          initial="hidden"
          animate="visible"
          className="relative z-10 max-w-7xl mx-auto px-6 text-center"
        >
          <motion.h1
            variants={heroItemVariants}
            className="font-heading font-black text-4xl md:text-5xl lg:text-6xl text-hvme-text-primary mb-4"
          >
            Agent{" "}
            <span className="gradient-text">Marketplace</span>
          </motion.h1>
          <motion.p
            variants={heroItemVariants}
            className="font-body text-lg md:text-xl text-hvme-text-secondary mb-8 max-w-2xl mx-auto"
          >
            Discover and deploy AI agents for your privacy-first workflows
          </motion.p>
          <SearchBar
            value={filters.search}
            onChange={(search) => setFilters((f) => ({ ...f, search }))}
          />
        </motion.div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        {/* Mobile Filter Toggle */}
        <div className="flex items-center justify-between mb-6 md:hidden">
          <FilterToggleButton
            isOpen={isMobileFilterOpen}
            onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
            activeCount={activeFilterCount}
          />
          <ResultsHeader
            count={filteredAgents.length}
            sortBy={filters.sortBy}
            onSortChange={(sortBy) => setFilters((f) => ({ ...f, sortBy }))}
          />
        </div>

        {/* Mobile Filter Panel */}
        <AnimatePresence>
          {isMobileFilterOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden overflow-hidden mb-6"
            >
              <div className="glass-panel rounded-2xl p-6">
                <MarketplaceFilters
                  filters={filters}
                  onFiltersChange={setFilters}
                  resultCount={filteredAgents.length}
                  onToggleCollapse={() => setIsMobileFilterOpen(false)}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <div className="hidden md:block">
            <div className="sticky top-24">
              <div className="glass-panel rounded-2xl overflow-hidden">
                <MarketplaceFilters
                  filters={filters}
                  onFiltersChange={setFilters}
                  resultCount={filteredAgents.length}
                  isCollapsed={!isFilterSidebarOpen}
                  onToggleCollapse={() => setIsFilterSidebarOpen(!isFilterSidebarOpen)}
                />
              </div>
            </div>
          </div>

          {/* Agent Grid */}
          <div className="flex-1">
            {/* Desktop Results Header */}
            <div className="hidden md:block">
              <ResultsHeader
                count={filteredAgents.length}
                sortBy={filters.sortBy}
                onSortChange={(sortBy) => setFilters((f) => ({ ...f, sortBy }))}
              />
            </div>

            {/* Grid or Empty State */}
            {filteredAgents.length > 0 ? (
              <AgentGrid agents={filteredAgents} onSelectAgent={handleSelectAgent} />
            ) : (
              <EmptyState onClearFilters={clearFilters} />
            )}
          </div>
        </div>
      </section>

      {/* Detail Modal */}
      <AgentDetailModal
        agent={selectedAgent}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onPurchase={handlePurchase}
        onDeploy={handleDeploy}
      />
    </div>
  );
}

export default MarketplacePage;
